import EditIcon from '@mui/icons-material/Edit';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import PersonIcon from '@mui/icons-material/Person';
import WorkIcon from '@mui/icons-material/Work';
import BadgeIcon from '@mui/icons-material/Badge';
import { Box, Button, Card, Divider, Grid, Paper, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import AvatarImage from "../components/common/AvatarImage";
import ChangePasswordModal from "../components/modals/ChangePasswordModal";
import UpdateAvatarModal from "../components/modals/UpdateAvatarModal";
import { t } from "../features/i18n";
import { RootState } from "../features/redux/store";
import { updateUserDetails } from "../features/redux/userSlice";

let color: string = "#9b4c97";
interface IProfile { }

const Profile: React.FC<IProfile> = () => {
  const [openChangePasswordModal, setOpenChangePasswordModal] = useState(false);
  const [openUpdateAvatarModal, setOpenUpdateAvatarModal] = useState(false);
  const dispatch = useDispatch();

  const role = useSelector((state: RootState) => state.user.role);
  const userName = useSelector((state: RootState) => state.user.userName);
  const userAvatar = useSelector((state: RootState) => state.user.avatar);
  const userEmail = useSelector((state: RootState) => state.user.email);
  const userCode = useSelector((state: RootState) => state.user.userCode);

  return (
    <Box sx={{ maxWidth: 800, margin: '0 auto', p: 2 }}>
      <Card
        elevation={3}
        sx={{
          borderTop: `8px solid ${color}`,
          borderRadius: 2,
          overflow: 'hidden'
        }}
      >
        <Box sx={{ p: 4 }}>
          <Typography variant="h4" sx={{ mb: 4, color: color, fontWeight: 'bold' }}>
            {t("user_profile")}
          </Typography>

          <Grid container spacing={4}>
            {/* Avatar Section */}
            <Grid item xs={12} md={4} sx={{ textAlign: 'center' }}>
              <Paper 
                elevation={2} 
                sx={{ 
                  p: 3, 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center',
                  gap: 2 
                }}
              >
                <AvatarImage
                  avatarUrl={userAvatar}
                  size={150}
                />
                <Button
                  variant="outlined"
                  onClick={() => setOpenUpdateAvatarModal(true)}
                  startIcon={<EditIcon />}
                  sx={{
                    color,
                    borderColor: color,
                    '&:hover': {
                      borderColor: color,
                      backgroundColor: `${color}10`,
                    }
                  }}
                >
                  {t("change_avatar")}
                </Button>
              </Paper>
            </Grid>

            {/* User Info Section */}
            <Grid item xs={12} md={8}>
              <Paper elevation={2} sx={{ p: 3 }}>
                <Stack spacing={3}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <PersonIcon sx={{ color }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {t("full_name")}
                      </Typography>
                      <Typography variant="h6">
                        {userName}
                      </Typography>
                    </Box>
                  </Box>

                  <Divider />

                  {/* User code */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <BadgeIcon sx={{ color }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {t('user_code_col')}
                      </Typography>
                      <Typography variant="h6">
                        {userCode}
                      </Typography>
                    </Box>
                  </Box>

                  <Divider />

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <EmailIcon sx={{ color }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {t("full_email")}
                      </Typography>
                      <Typography variant="h6">
                        {userEmail}
                      </Typography>
                    </Box>
                  </Box>

                  <Divider />

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <WorkIcon sx={{ color }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {t("user_role")}
                      </Typography>
                      <Typography variant="h6">
                        {role}
                      </Typography>
                    </Box>
                  </Box>

                  <Divider />

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Button
                      variant="outlined"
                      onClick={() => setOpenChangePasswordModal(true)}
                      startIcon={<LockIcon />}
                      sx={{
                        color,
                        borderColor: color,
                        '&:hover': {
                          borderColor: color,
                          backgroundColor: `${color}10`,
                        }
                      }}
                    >
                      {t("change_password")}
                    </Button>
                  </Box>
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </Card>

      {/* Modals */}
      <ChangePasswordModal
        open={openChangePasswordModal}
        onClose={() => setOpenChangePasswordModal(false)}
      />
      <UpdateAvatarModal
        open={openUpdateAvatarModal}
        onClose={() => setOpenUpdateAvatarModal(false)}
        currentAvatar={userAvatar}
        onAvatarUpdate={async (avatarUrl: string) => {
          dispatch(updateUserDetails({ avatar: avatarUrl }));
        }}
      />
    </Box>
  );
};

export default Profile;
